/**
 * Ziggy's variant picker — client pass. Live asset: assets/variant-avail.js
 *
 * snippets/variant-main-picker.liquid renders the truth (which option values are
 * buyable, cascade Colour → Size → Spec, swatches, size order, legends) and Horizon
 * re-fetches + morphs that snippet on every click. This file only makes it instant
 * and keeps the form honest in the ~300 ms before the fetch lands:
 *
 *   1. `change` (capture, before Horizon reads the checked inputs):
 *      re-run the cascade on the DOM, hide/show pills, move the checked input to a
 *      visible value when needed, write input[name=id], set the Add-to-cart button.
 *   2. `variant:update` (bubble, after Horizon's product-form handler):
 *      same pass, so anything Horizon undid is put back in the same task (no paint
 *      in between → no flicker).
 *   3. init: same pass; if the server's selection had to be corrected, fire `change`
 *      so Horizon refreshes price / gallery for the corrected variant.
 *
 * Data: <script id="zc-variants-json"> → [{ id, o: [opt1, opt2, opt3], b: 0|1, so: 0|1|2 }]
 *   b  = buyable, so = 0 none · 1 "Special order will take a few extra days" · 2 "Available — may take a few extra days"
 *
 * Never show Default / Default Title leftovers. Prefer a buyable variant that
 * does not use those values. No product-type branches — change Liquid rules first.
 */
(function () {
  "use strict";

  var PICKER = "variant-picker[data-zc-avail-picker]";
  var LEAD_COPY = { 1: "Special order will take a few extra days", 2: "Available — may take a few extra days" };
  var LEAD_TITLE = {
    1: "Special order · will take a few extra days",
    2: "Special order · available, may take a few extra days",
  };

  // ---------------------------------------------------------------- data

  function readVariants(picker) {
    var el = picker.querySelector("#zc-variants-json");
    if (!el) return [];
    try {
      var rows = JSON.parse(el.textContent) || [];
      return rows.map(function (r) {
        return {
          id: String(r.id),
          o: (r.o || []).map(function (v) {
            return v == null ? "" : String(v);
          }),
          b: r.b === 1 || r.b === true,
          so: Number(r.so) || 0,
          m: r.m ? String(r.m) : "",
        };
      });
    } catch (e) {
      return [];
    }
  }

  function fieldsets(picker) {
    return Array.prototype.slice.call(picker.querySelectorAll("fieldset.variant-option"));
  }

  function isColorFieldset(fs) {
    if (fs.getAttribute("data-zc-color-option") === "true") return true;
    var name = (fs.getAttribute("data-option-name") || "").toLowerCase();
    return name === "color" || name === "colour";
  }

  function isJunkOptionValue(value) {
    var n = String(value || "").trim().toLowerCase();
    return n === "default" || n === "default title";
  }

  function junkScore(row) {
    var n = 0;
    for (var i = 0; i < row.o.length; i++) {
      if (isJunkOptionValue(row.o[i])) n++;
    }
    return n;
  }

  function optionIndex(fs, fallback) {
    var pos = Number(fs.getAttribute("data-option-position"));
    return pos > 0 ? pos - 1 : fallback;
  }

  function checkedInput(fs) {
    return fs.querySelector('input[type="radio"]:checked');
  }

  function matchesEarlier(row, selections, upto) {
    for (var j = 0; j < upto; j++) {
      if (selections[j] == null) continue;
      if (row.o[j] !== selections[j]) return false;
    }
    return true;
  }

  // ---------------------------------------------------------------- DOM writes

  function setHidden(label, input, hidden) {
    var isHidden = label.getAttribute("data-zc-hidden") === "true";
    if (hidden === isHidden && input.getAttribute("data-option-available") === (hidden ? "false" : "true")) {
      return;
    }
    if (hidden) {
      label.setAttribute("data-zc-hidden", "true");
      label.setAttribute("aria-disabled", "true");
      input.setAttribute("aria-disabled", "true");
      input.setAttribute("data-option-available", "false");
    } else {
      label.removeAttribute("data-zc-hidden");
      label.removeAttribute("aria-disabled");
      input.removeAttribute("aria-disabled");
      input.setAttribute("data-option-available", "true");
    }
  }

  function check(fs, input) {
    fs.querySelectorAll('input[type="radio"]').forEach(function (other) {
      var on = other === input;
      if (other.checked !== on) other.checked = on;
      other.setAttribute("data-current-checked", on ? "true" : "false");
    });
  }

  function productForm(picker) {
    var section = picker.closest(".shopify-section") || document;
    return (
      section.querySelector("product-form-component[data-product-tags]") ||
      section.querySelector("product-form-component") ||
      document.querySelector("product-form-component[data-product-tags]") ||
      document.querySelector("product-form-component")
    );
  }

  function writeVariantId(picker, id) {
    var form = productForm(picker);
    var hidden = form && form.querySelector('input[name="id"]');
    if (!hidden || !id) return;
    if (String(hidden.value || "") !== id) {
      hidden.value = id;
      hidden.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (picker.getAttribute("data-template-product-match") === "true") {
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get("variant") !== id) {
          url.searchParams.set("variant", id);
          history.replaceState({}, "", url.toString());
        }
      } catch (e) {
        /* ignore */
      }
    }
  }

  function setAttr(el, name, value) {
    if (value === null) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
    } else if (el.getAttribute(name) !== value) {
      el.setAttribute(name, value);
    }
  }

  // Every write below is change-only: Horizon's sticky bar observes the main button
  // and re-renders on any attribute mutation, so no-op writes must not happen.
  function setButton(button, enabled, label) {
    if (!button) return;
    if (button.disabled !== !enabled) button.disabled = !enabled;
    setAttr(button, "disabled", enabled ? null : "");
    setAttr(button, "aria-disabled", enabled ? "false" : "true");
    var targets = button.querySelectorAll(".add-to-cart-text__content");
    if (!targets.length) return;
    targets.forEach(function (el) {
      // innermost text node holder; never touch the icon wrapper
      var leaf = el;
      while (leaf.children.length === 1 && leaf.children[0].tagName === "SPAN") leaf = leaf.children[0];
      if (leaf.querySelector("svg")) return;
      if ((leaf.textContent || "").trim() !== label) leaf.textContent = label;
    });
  }

  function syncButtons(picker, match) {
    var form = productForm(picker);
    if (!form) return;
    var enabled = !!match;
    var label = !match ? "Sold out" : match.so ? "Special order" : "Add to cart";

    var buttons = form.querySelectorAll('add-to-cart-component [ref="addToCartButton"], button[name="add"]');
    var container = form.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
    var currentlyEnabled = buttons.length ? !buttons[0].disabled : enabled;
    if (container && currentlyEnabled !== enabled) {
      if (enabled && typeof container.enable === "function") container.enable();
      else if (!enabled && typeof container.disable === "function") container.disable();
    }
    buttons.forEach(function (b) {
      setButton(b, enabled, label);
    });
    // The sticky bar is Horizon's puppet of this button and is re-morphed from the
    // server HTML on every variant:update — writing to it directly trips its observer.

    // lead-time note under the button
    var note = document.getElementById("zc-so-lead-time");
    var copy = match && match.so ? LEAD_COPY[match.so] || "" : "";
    if (!note && copy) {
      note = document.createElement("p");
      note.id = "zc-so-lead-time";
      note.className = "zc-so-lead-time";
      var host = form.querySelector("add-to-cart-component") || form.querySelector(".product-form-buttons") || form;
      if (host.parentNode) host.parentNode.insertBefore(note, host.nextSibling);
    }
    if (note) {
      if (copy && note.textContent !== copy) note.textContent = copy;
      if (note.hidden !== !copy) note.hidden = !copy;
      var display = copy ? "" : "none";
      if (note.style.display !== display) note.style.display = display;
    }
  }

  function syncLegends(picker, fss) {
    fss.forEach(function (fs) {
      var val = fs.querySelector("legend .variant-option__swatch-value");
      if (!val) return;
      var input = checkedInput(fs);
      var text = input ? input.value : "";
      if (val.textContent !== text) val.textContent = text;
    });
  }

  /** Mark pills whose every buyable variant is special-order (tooltip + CSS hook). */
  function markSpecialOrderPills(fss, buyable, selections) {
    fss.forEach(function (fs, i) {
      var idx = optionIndex(fs, i);
      var color = isColorFieldset(fs);
      fs.querySelectorAll(".variant-option__button-label").forEach(function (label) {
        var input = label.querySelector('input[type="radio"]');
        if (!input || label.getAttribute("data-zc-hidden") === "true") return;
        var so = 0;
        var any = false;
        for (var r = 0; r < buyable.length; r++) {
          var row = buyable[r];
          if (row.o[idx] !== input.value) continue;
          if (!color && !matchesEarlier(row, selections, idx)) continue;
          any = true;
          if (!row.so) {
            so = 0;
            break;
          }
          so = so || row.so;
        }
        var mark = any && so > 0;
        if (mark) {
          if (label.getAttribute("data-zc-so-only") !== "true") label.setAttribute("data-zc-so-only", "true");
          if (!label.classList.contains("variant-option__button-label--has-swatch")) {
            label.setAttribute("title", LEAD_TITLE[so]);
          }
        } else if (label.getAttribute("data-zc-so-only") === "true") {
          label.removeAttribute("data-zc-so-only");
          if (label.getAttribute("title") && label.getAttribute("title").indexOf("Special order") === 0) {
            label.removeAttribute("title");
          }
        }
      });
    });
  }

  // ---------------------------------------------------------------- the pass

  /**
   * Re-derive the picker from the variant table + current checked inputs.
   * @returns {{ corrected: boolean, match: object|null, input: HTMLInputElement|null }}
   */
  function apply(picker) {
    var rows = readVariants(picker);
    var buyable = rows.filter(function (r) {
      return r.b;
    });
    var fss = fieldsets(picker);
    var result = { corrected: false, match: null, input: null };
    if (!fss.length) return result;

    if (!buyable.length) {
      // Nothing buyable at all: leave Horizon's native rendering alone, just make sure the button says so.
      syncButtons(picker, null);
      return result;
    }

    var selections = [];
    fss.forEach(function (fs, i) {
      var idx = optionIndex(fs, i);
      var color = isColorFieldset(fs);
      var firstVisible = null;
      var checked = checkedInput(fs);
      var checkedVisible = false;

      fs.querySelectorAll(".variant-option__button-label").forEach(function (label) {
        var input = label.querySelector('input[type="radio"]');
        if (!input) return;
        if (isJunkOptionValue(input.value)) {
          setHidden(label, input, true);
          return;
        }
        var show = false;
        for (var r = 0; r < buyable.length; r++) {
          var row = buyable[r];
          if (row.o[idx] !== input.value) continue;
          if (color || matchesEarlier(row, selections, idx)) {
            show = true;
            break;
          }
        }
        setHidden(label, input, !show);
        if (show && !firstVisible) firstVisible = input;
        if (show && input === checked) checkedVisible = true;
      });

      if (!checkedVisible && firstVisible) {
        check(fs, firstVisible);
        result.corrected = true;
        result.input = firstVisible;
        checked = firstVisible;
      }
      selections[idx] = checked ? checked.value : null;
    });

    var match = null;
    var matchJunk = 999;
    for (var r = 0; r < buyable.length; r++) {
      if (!matchesEarlier(buyable[r], selections, selections.length)) continue;
      var score = junkScore(buyable[r]);
      if (!match || score < matchJunk) {
        match = buyable[r];
        matchJunk = score;
        if (score === 0) break;
      }
    }
    result.match = match;

    if (match) writeVariantId(picker, match.id);
    syncButtons(picker, match);
    syncLegends(picker, fss);
    markSpecialOrderPills(fss, buyable, selections);
    syncGallery(picker, match, selections);
    return result;
  }

  function selectedColor(picker, selections) {
    var fss = fieldsets(picker);
    for (var i = 0; i < fss.length; i++) {
      if (!isColorFieldset(fss[i])) continue;
      var idx = optionIndex(fss[i], i);
      if (selections && selections[idx]) return selections[idx];
      var input = checkedInput(fss[i]);
      if (input) return input.value;
    }
    return "";
  }

  function slideMediaId(el) {
    var raw = el && el.getAttribute ? el.getAttribute("data-media-id") : "";
    if (!raw && el && el.querySelector) {
      var nested = el.querySelector("[data-media-id]");
      if (nested) raw = nested.getAttribute("data-media-id") || "";
    }
    var digits = String(raw || "").match(/(\d+)\s*$/);
    return digits ? digits[1] : String(raw || "");
  }

  function syncGallery(picker, match, selections) {
    var gallery = document.querySelector("media-gallery");
    var slideshow = gallery && gallery.slideshow;
    if (!slideshow || typeof slideshow.select !== "function") return;

    var slides = slideshow.refs && slideshow.refs.slides
      ? slideshow.refs.slides
      : Array.prototype.slice.call(slideshow.querySelectorAll("[slide-id], slideshow-slide, [data-media-id]"));
    if (!slides || !slides.length) return;

    var mediaId = match && match.m ? String(match.m).match(/(\d+)\s*$/) : null;
    mediaId = mediaId ? mediaId[1] : match && match.m ? String(match.m) : "";
    var color = selectedColor(picker, selections);
    var marker = color ? " — " + color : "";
    var colorTail = color ? new RegExp("(?:—|,)\\s*" + color.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b", "i") : null;
    var index = -1;

    for (var i = 0; i < slides.length; i++) {
      var el = slides[i];
      if (!el) continue;
      var id = slideMediaId(el);
      var img = el.querySelector("img");
      var alt = img ? String(img.alt || "") : "";
      var keep = false;
      if (mediaId && id && id === mediaId) keep = true;
      else if (marker && alt.indexOf(marker) !== -1) keep = true;
      else if (colorTail && colorTail.test(alt)) keep = true;
      else if (!color) keep = true;
      if (keep) {
        el.removeAttribute("data-zc-color-hidden");
        if (index < 0) index = i;
      } else {
        el.setAttribute("data-zc-color-hidden", "true");
      }
    }

    // Never leave the hero blank — if this color has no tagged photo, show the assigned media or everything.
    if (index < 0) {
      for (var j = 0; j < slides.length; j++) {
        if (slides[j]) slides[j].removeAttribute("data-zc-color-hidden");
      }
      return;
    }
    try {
      slideshow.select(index, undefined, { animate: false });
    } catch (e) {}
  }

  // ---------------------------------------------------------------- wiring

  var applying = false;
  function run(picker) {
    if (applying || !picker) return null;
    applying = true;
    try {
      return apply(picker);
    } finally {
      applying = false;
    }
  }

  function pickers() {
    return Array.prototype.slice.call(document.querySelectorAll(PICKER));
  }

  function init() {
    pickers().forEach(function (picker) {
      if (picker.getAttribute("data-zc-init") === "true") return;
      picker.setAttribute("data-zc-init", "true");
      var res = run(picker);
      // Server selection wasn't buyable (stale ?variant=, first-available not buyable):
      // let Horizon fetch the corrected variant so price/gallery/buy-buttons follow.
      if (res && res.corrected && res.input) {
        res.input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  // 1. Before Horizon builds its request from the checked inputs.
  document.addEventListener(
    "change",
    function (e) {
      var t = e.target;
      if (!(t instanceof HTMLInputElement) || t.type !== "radio") return;
      var picker = t.closest(PICKER);
      if (!picker) return;
      run(picker);
    },
    true
  );

  // 2. After Horizon morphed the picker and product-form handled the update.
  document.addEventListener("variant:update", function (e) {
    var picker = e.target && e.target.closest ? e.target.closest(PICKER) : null;
    if (!picker) picker = document.querySelector(PICKER);
    run(picker);
  });

  // 3. Page load, and pickers that arrive later (quick-add dialogs).
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("dialog:open", init);
  document.addEventListener("quick-add:open", init);
})();
